import React from "react";
import { EmailPreviewDialog, resolveCustomerReplyTo } from "./EmailPreviewDialog";

/**
 * Read-only preview of the Google Review Request email.
 *
 * Mirrors backend `_build_review_request_email`:
 *   • Subject: "How was your cleaning service?"
 *   • Body interpolates the customer name and the owner's business name.
 *   • Renders the same "Leave a Google review" CTA button the customer sees,
 *     pointing at the owner's saved `google_review_link`.
 *
 * Display only — opening this dialog never triggers a send. The Request
 * Google Review button next to the trigger remains the only thing that
 * actually fires `/api/leads/{id}/request-review`.
 *
 * Reply-To follows the shared `resolveCustomerReplyTo` chain so it stays
 * in lockstep with the backend resolver and with `RebookPreviewDialog`.
 */
export const ReviewRequestPreviewDialog = ({
  open,
  onOpenChange,
  customerName,
  user,
  reviewLink = null,
}) => {
  const businessName = (user?.business_name || "your cleaner").trim();
  const safeName = (customerName || "there").trim() || "there";
  const replyTo = resolveCustomerReplyTo(user);

  return (
    <EmailPreviewDialog
      open={open}
      onOpenChange={onOpenChange}
      subject="How was your cleaning service?"
      replyTo={replyTo}
      sendButtonLabel="Request Google Review"
      testidPrefix="review-preview"
    >
      <div
        className="rounded-md border border-gray-200 bg-white px-4 py-3 text-gray-800 leading-relaxed whitespace-pre-line"
        data-testid="review-preview-body"
      >
        {`Hi ${safeName},

Thank you for choosing ${businessName}! We hope you're happy with your cleaning service.

If you have a minute, we'd really appreciate a quick Google review. It helps our small business a lot:`}
        {reviewLink ? (
          <div className="my-3" data-testid="review-preview-cta">
            <a
              href={reviewLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-4 py-2 rounded-md bg-green-800 hover:bg-green-900 text-white text-sm font-semibold no-underline"
              data-testid="review-preview-cta-link"
            >
              Leave a Google review
            </a>
          </div>
        ) : null}
        {`Thank you,
${businessName}`}
      </div>
    </EmailPreviewDialog>
  );
};
